/**
 * The Fleet feed panel (M6 plan P35): recent asset alerts with their *jump to* / *send wing* buttons (offered by the
 * feature's rule), then the captain logs and claim changes, newest first. The body of the dashboard's Fleet › Feed
 * section (W3 card M2): the shell owns the frame, Close and the keys. UI events only.
 */
import type { ReactNode, Ref } from 'react'
import type { FeedRow, FleetFeedLabels } from './model'
import '../../ui/strategic/strategic.css'

export interface FeedAlertRow {
  key: string
  text: string
  jump: boolean
  wing: boolean
}

export interface FeedPanelProps {
  labels: FleetFeedLabels
  alerts: readonly FeedAlertRow[]
  rows: readonly FeedRow[]
  onAction(key: string, action: 'jumpTo' | 'sendWing'): void
  /** The panel element (the section's button navigation). */
  panelRef?: Ref<HTMLDivElement>
}

export function FeedPanel(p: FeedPanelProps): ReactNode {
  const l = p.labels
  return (
    <div className="sui ff-panel" role="region" aria-label={l.title} data-testid="fleet-feed" ref={p.panelRef}>
      <section className="sui-section">
        <h3 className="sui-section__title">{l.alerts}</h3>
        {p.alerts.length === 0 ? <p className="sui-empty">{l.noAlerts}</p> : null}
        {p.alerts.map((a) => (
          <div key={a.key} className="sui-line" data-testid="fleet-feed-alert">
            <span className="sui-line__label">{a.text}</span>
            {a.jump ? (
              <button type="button" className="sui-btn" data-testid="fleet-alert-jump" onClick={() => p.onAction(a.key, 'jumpTo')}>
                {l.jumpTo}
              </button>
            ) : null}
            {a.wing ? (
              <button type="button" className="sui-btn" data-testid="fleet-alert-wing" onClick={() => p.onAction(a.key, 'sendWing')}>
                {l.sendWing}
              </button>
            ) : null}
          </div>
        ))}
      </section>
      <section className="sui-section">
        <h3 className="sui-section__title">{l.feed}</h3>
        {p.rows.length === 0 ? <p className="sui-empty">{l.empty}</p> : null}
        <ol className="sui-list">
          {p.rows.map((r) => (
            <li key={r.key} className="sui-item" data-kind={r.kind}>
              <span className="sui-item__meta">
                {l.day} {r.day}
              </span>
              <span className="sui-item__headline">{r.text}</span>
            </li>
          ))}
        </ol>
      </section>
    </div>
  )
}
